/** @jsxImportSource @emotion/react */
import React, { useRef, useImperativeHandle, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { Button, Checkbox } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { findMultiUtMatrixLibraryList, findUtMatrixLibraryList } from 'apis/ut/UtMatrixLibrary';
import { UtMatrixLibrary } from 'models/ut/UtMatrixLibrary';
import { hasRole } from 'utils/SessionUtil';
import ApproveRequestModal from '../../approves/ApproveRequestModal';
import { st } from 'components/inputs/CustomInput';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';
import GridSettingButtons from 'components/grids/GridSettingButtons';
interface Props {
  // masterRef: any;
  onGridRef: (ref: any) => void;
  onClickStndConfirmBtn: () => void;
  onClickRegistBtn: () => void;
  onClickEditBtn: () => void;
  // onClickDeleteBtn: () => void;
  onClickLibraryExcelDownloadBtn: () => void;
  onClickExcelDownloadBtn: () => void;
}

/**
 * @desc Library > 그리드 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.05   jshwang12       [고도화] 전력/CDA 실사용량 추가 (EUM Tag 최대 수치값 노출(저장X))
 * 2025.10.05   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리) (ex. 230.456 입력 시 실제 데이터는 230.5 저장)
 * 2026.08.05   jihwan89        CSR E20260727-000228 Vacuum Pump 추가
 */
export const UtMatrixLibraryGrid = (
  {
    // masterRef,
    onGridRef,
    onClickStndConfirmBtn,
    onClickRegistBtn,
    onClickEditBtn,
    // onClickDeleteBtn,
    onClickLibraryExcelDownloadBtn,
    onClickExcelDownloadBtn,
  }: Props,
  ref
) => {
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);
  const { t } = useTranslation();

  const [subTitle, setSubTitle] = useState<string>('');
  const [rowData, setRowData] = useState<UtMatrixLibrary[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [condition, setCondition] = useState({
    stndEqpId: '',
    curVerYn: 'Y',
    utmLibId: '',
    prjNm: '',
    vdcpNm: '',
  });

  const curVerYn = useRef('Y');

  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const [requestModalCondition, setRequestModalCondition] = useState<any>();
  const [isOpenRequestModal, setOpenRequestModal] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    init: () => {
      setTotalCount(0);
      setRowData([]);
      setSubTitle('');
    },
    search: async (params) => {
      await handleSearch(params);
    },
    getSelectedRow: () => getSelectedRow(),
  }));

  const getSelectedRow = () => {
    return gridRef.current.rows.filter((r) => r.isSelected).map((o) => o.dataItem);
  };

  useEffect(() => {
    if (hasRole('ADM') || hasRole('UT_MANAGER')) {
      setHasAuth(true);
    }
  }, []);

  const handleSearch = async (params) => {
    let response;
    let searchCondition;

    if (Array.isArray(params) && params.length > 1) {
      const ids = params.map((item) => item.stndEqpId);
      searchCondition = {
        ...condition,
        stndEqpId: '',
        stndEqpIds: ids,
        curVerYn: curVerYn.current,
      };

      setCondition(searchCondition);
      response = await findMultiUtMatrixLibraryList(searchCondition);
    } else {
      let stndEqpId = '';
      if (params && Object.keys(params).length > 0) {
        stndEqpId = params.stndEqpId;
      }
      searchCondition = {
        ...condition,
        ...params,
        stndEqpIds: [],
        stndEqpId: stndEqpId,
        curVerYn: curVerYn.current,
      };
      setCondition(searchCondition);
      response = await findUtMatrixLibraryList(searchCondition);
    }
    const title =
      (searchCondition?.stndEqpId || '') === 'No Selection' ? '' : searchCondition?.stndEqpId || '';
    setSubTitle(title);
    setTotalCount(response?.length || 0);

    if (response) {
      setRowData(response);
    }
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'prdnProcCdNm',
        header: String(t('ut.label.공정', '공정')),
        align: 'center',
        minWidth: 70,
        width: 100,
      },
      {
        binding: 'eqclIdNm',
        header: String(t('ut.label.설비군명', '설비군명')),
        align: 'center',
        minWidth: 70,
        width: 140,
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        align: 'center',
        minWidth: 70,
        width: 122,
      },
      {
        binding: 'dtalProcCdNm',
        header: String(t('ut.label.세부공정명', '세부공정명')),
        align: 'left',
        minWidth: 70,
        width: 150,
      },
      {
        binding: 'eqpMainNm',
        header: String(t('ut.label.Main 명', 'Main 명')),
        align: 'left',
        minWidth: 70,
        width: 110,
      },
      {
        binding: 'eqpMchNm',
        header: String(t('ut.label.Machine 명', 'Machine 명')),
        align: 'left',
        minWidth: 70,
        width: 140,
      },
      {
        binding: 'utmLibId',
        header: String(t('ut.label.Library ID', 'Library ID')),
        align: 'center',
        minWidth: 70,
        width: 140,
      },
      {
        header: String(t('ut.label.Version', 'Version')),
        binding: 'utmLibVerNo',
        align: 'center',
        cssClass: 'noMerge',
        minWidth: 60,
        width: 80,
      },
      {
        binding: 'prjNm',
        header: String(t('ut.label.프로젝트', '프로젝트')),
        align: 'center',
        cssClass: 'noMerge',
        minWidth: 70,
        width: 100,
      },
      {
        binding: 'vdcpNm',
        header: String(t('ut.label.Vendor', 'Vendor')),
        align: 'center',
        minWidth: 70,
        width: 100,
      },
      {
        binding: 'genNm',
        header: String(t('ut.label.Generation', 'Generation')),
        align: 'center',
        minWidth: 70,
        width: 115,
      },
      {
        header: String(t('ut.label.Electricity', 'Electricity')),
        collapseTo: 'vltgNvl',
        columns: [
          {
            binding: 'vltgNvl',
            header: String(t('ut.label.Voltage', 'Voltage')),
            align: 'center',
            minWidth: 70,
            width: 90,
          },
          {
            binding: 'elpwPhasCd',
            header: String(t('ut.label.Phase', 'Phase')),
            align: 'center',
            minWidth: 60,
            width: 75,
          },
          {
            binding: 'ctwTpNm',
            header: String(t('ut.label.Wire', 'Wire')),
            align: 'center',
            minWidth: 60,
            width: 90,
          },
          {
            binding: 'od2EcNvl',
            header: String(t('ut.label.Current(입력값)', 'Current(입력값)')),
            align: 'right',
            minWidth: 60,
            width: 120,
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
          {
            binding: 'brkeCapa',
            header: String(t('ut.label.Breaker', 'Breaker')),
            minWidth: 60,
            width: 90,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
          {
            binding: 'elpwCapa',
            header: String(t('ut.label.Capacity', 'Capacity')),
            minWidth: 60,
            width: 90,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
          {
            binding: 'elpwEumMaxNvl',
            header: String(t('ut.label.실사용량', '실사용량')),
            minWidth: 60,
            width: 90,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        binding: 'cdaCspCapa',
        header: String(t('ut.label.CDA', 'CDA')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cssClass: 'noMerge',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        header: String(t('ut.label.CDA', 'CDA')),
        columns: [
          {
            binding: 'cdaEumMaxNvl',
            header: String(t('ut.label.실사용량', '실사용량')),
            minWidth: 60,
            width: 90,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        binding: 'nitrPeakCspCapa',
        header: String(t('ut.label.N2', 'N2')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'iwCspCapa',
        header: String(t('ut.label.IW', 'IW')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'frwtCspCapa',
        header: String(t('ut.label.FW', 'FW')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'coolCspCapa',
        header: String(t('ut.label.PCW', 'PCW')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'wwCspCapa',
        header: String(t('ut.label.WW', 'WW')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'stemCspCapa',
        header: String(t('ut.label.ST', 'ST')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'lngCspCapa',
        header: String(t('ut.label.NG', 'NG')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'insdExhaCspCapa',
        header: String(t('ut.label.RA', 'RA')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'otsdExhaCspCapa',
        header: String(t('ut.label.EA', 'EA')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
      {
        binding: 'vacmPumpCspCapa',
        header: String(t('ut.label.VP', 'VP')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'suarCspCapa',
        header: String(t('ut.label.SA', 'SA')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'rmk',
        header: String(t('ut.label.Remark', 'Remark')),
        align: 'left',
        minWidth: 60,
        width: 110,
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('ut.label.등록자', '등록자')),
        align: 'center',
        minWidth: 60,
        width: 100,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('ut.label.등록일자', '등록일자')),
        align: 'center',
        minWidth: 60,
        width: 100,
      },
    ];
  }, [isChangeColumn, rowData]);

  const onInitialized = (grid) => {
    onGridRef && onGridRef(grid);
    gridRef.current = grid;
  };

  const exportFormat = (args) => {
    const p = args.panel;
    const row = args.row;
    const col = args.col;
    const xlsxCell = args.xlsxCell;
    if (p.columns[col].binding === 'gatingContIds') {
      const cell = args.getFormattedCell();
      xlsxCell.value = cell.textContent;
    }
    if (p.columns[col].binding === 'gatingContTlIds') {
      const cell = args.getFormattedCell();
      xlsxCell.value = cell.textContent;
    }
    if (p.columns[col].binding === 'gatingEqpContData') {
      const cell = args.getFormattedCell();
      xlsxCell.value = '';
    }
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('mp.label.UT Matrix Library', 'UT Matrix Library')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{totalCount.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {subTitle && <div className="info warning">{subTitle}</div>}
        </SubTitleGroup>
        <ControlBtnGroup>
          <div style={{ display: 'inline-flex', width: '100px', paddingTop: '5px' }}>
            <label htmlFor="curVerYn_detail" style={{ paddingTop: '0px' }}>
              {t('ut.label.이전버전보기', '이전버전보기')}
            </label>
            <Checkbox
              id="curVerYn_detail"
              css={st.checkbox}
              className="custom"
              checked={curVerYn.current === 'N'}
              onChange={(e) => {
                curVerYn.current = curVerYn.current === 'Y' ? 'N' : 'Y';
                handleSearch([]);
              }}
              disableRipple
            />
          </div>
          <Button
            css={IconButton.button}
            className="info"
            onClick={onClickStndConfirmBtn}
            disableRipple
          >
            {t('com.button.설비코드확인', '설비코드확인')}
          </Button>
          <Button
            css={IconButton.button}
            className="regist"
            onClick={onClickRegistBtn}
            disableRipple
          >
            {t('com.button.등록', '등록')}
          </Button>
          <Button css={IconButton.button} className="edit" onClick={onClickEditBtn} disableRipple>
            {t('com.button.수정', '수정')}
          </Button>
          {/*<Button css={IconButton.button} className="delete" onClick={onClickEditBtn} disableRipple>*/}
          {/*  {t('com.button.삭제', '삭제')}*/}
          {/*</Button>*/}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={onClickLibraryExcelDownloadBtn}
            disableRipple
          >
            {t('com.button.Library 다운로드', 'Library 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={onClickExcelDownloadBtn}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={handleSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'utLibrayGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              gridRef.current.refresh();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'utLibrayGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        autoCheck={true}
        isReadOnly={true}
        allowDragging={false}
        excludeFilter={['utmLibId']}
        excludePin={[
          'prdnProcCdNm',
          'eqclIdNm',
          'stndEqpId',
          'dtalProcCdNm',
          'eqpMainNm',
          'eqpMchNm',
          'utmLibId',
          'utmLibVerNo',
          'prjNm',
          'vdcpNm',
          'genNm',
          'vltgNvl',
          'elpwPhasCd',
          'ctwTpCd',
          'od2EcNvl',
          'brkeCapa',
          'elpwCapa',
          'elpwEumMaxNvl',
          'cdaCspCapa',
          'cdaEumMaxNvl',
          'nitrPeakCspCapa',
          'iwCspCapa',
          'frwtCspCapa',
          'coolCspCapa',
          'wwCspCapa',
          'stemCspCapa',
          'lngCspCapa',
          'insdExhaCspCapa',
          'otsdExhaCspCapa',
          'vpCspCapa',
          'suarCspCapa',
          'rmk',
          'ctwTpNm',
          'dataInsUserNm',
          'dataInsDtm',
        ]}
        height={550}
        initialized={onInitialized}
      />
      {isOpenRequestModal && (
        <ApproveRequestModal
          open={isOpenRequestModal}
          close={() => setOpenRequestModal(false)}
          pageId={requestModalCondition?.pageId}
          aprReqId={requestModalCondition?.aprReqId}
          condition={{
            mode: requestModalCondition?.mode,
            requestMasterList: requestModalCondition?.requestMasterList,
          }}
        />
      )}
    </>
  );
};

export default React.forwardRef(UtMatrixLibraryGrid);
